import {Box, Button, Chip, Stack} from '@mui/material'
import type {GridColDef, GridRenderCellParams, GridSortModel} from '@mui/x-data-grid'
import type {FC} from 'react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useLazyGetAnomaliesQuery} from '@apis/anomalies'
import {useGetEquipmentsWithSearchQuery, useGetLinesWithSearchQuery} from '@apis/base'
import {useGetProductionAreasQuery} from '@apis/settings'
import AnomalyFilterByDetectionType from '@app/containers/Dashboards/Anomalies/Components/AnomalyFilterByDetectionType'
import AnomalyFilterByStatus from '@app/containers/Dashboards/Anomalies/Components/AnomalyFilterByStatus'
import GoToAnomaly from '@app/containers/Dashboards/Anomalies/Components/GoToAnomalyButton'
import {getFormattedDate} from '@app/util-functions'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useUserContext} from '@containers/App/UserProvider'
import AnomalyChatModal from '@containers/Dashboards/Anomalies/AnomalyChatFeedback/AnomalyFeedbackChatModal'
import BasicDataGrid from '@shared/components/base/BasicDataGrid/BasicDataGrid'
import BasicHierarchyFilterGroup from '@shared/components/base/filters/BasicHierarchyFilterGroup'
import BasicHierarchyFiltersRow from '@shared/components/base/filters/BasicHierarchyFiltersRow'
import Picker from '@shared/components/base/inputs/Picker'
import StepDescription from '@shared/components/StepDescription'
import TrueFalseIconBasicComponent from '@shared/components/TrueFalseIconBasicComponent'
import {getCriticalityChipSx, getCriticalityLabel} from '@shared/constants/criticality'
import {tablesFullPage} from '@shared/constants/lineManagement'
import {
  DEFAULT_OFFSET,
  DEFAULT_PAGE_SIZE,
  STANDARD_PAGE_SIZE_OPTIONS,
} from '@shared/constants/preferences/pagination'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import type {AnomalyWithReasonInterface} from '@shared/interfaces/anomaly'

type AnomalyTableProps = {
  embedded?: boolean
  onSelect?: (anomaly: AnomalyWithReasonInterface) => void
  selectedIds?: string[]
  height?: number | string
  initialFilters?: {
    productionAreaId?: string
    lineId?: string
    equipmentId?: string
    status?: string
    detectionType?: string
  }
  hideChat?: boolean
  hideGoTo?: boolean
}

const AnomalyTable: FC<AnomalyTableProps> = ({
  embedded = false,
  onSelect,
  selectedIds = [],
  height,
  initialFilters,
  hideChat = false,
  hideGoTo = false,
}) => {
  // States
  const [paginationModel, setPaginationModel] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_OFFSET,
  })
  const {currentUser} = useUserContext() as UserContextProps
  const [searchParams] = useSearchParams()
  const [localFilters, setLocalFilters] = useState({
    productionAreaId: initialFilters?.productionAreaId || '',
    lineId: initialFilters?.lineId || '',
    equipmentId: initialFilters?.equipmentId || '',
    status: initialFilters?.status || 'All',
    detectionType: initialFilters?.detectionType || 'All',
  })
  const [sortModel, setSortModel] = useState<GridSortModel>([{field: 'created_at', sort: 'desc'}])

  // Basic Variables
  const currentEquipmentId = embedded
    ? localFilters.equipmentId
    : searchParams.get('equipmentId') || searchParams.get('equipment') || ''
  const currentLineId = embedded
    ? localFilters.lineId
    : searchParams.get('lineId') || searchParams.get('line') || ''
  const currentProductionAreaId = embedded
    ? localFilters.productionAreaId
    : searchParams.get('productionAreaId') || ''
  const currentStatus = embedded ? localFilters.status : searchParams.get('status') || 'All'
  const currentDetectionType = embedded
    ? localFilters.detectionType
    : searchParams.get('detection_type') || 'All'

  // Hooks
  const [t, i18n] = useCustomTranslation('anomalies')
  const [tCommon] = useCustomTranslation('common')

  // RTK Queries
  const [searchState, setSearchState] = useState({
    area: '',
    line: '',
    equipment: '',
  })
  const {data: productionAreasRaw, isFetching: isFetchingAreas} = useGetProductionAreasQuery({
    search: searchState.area,
  })
  const {data: lines = [], isFetching: isFetchingLines} = useGetLinesWithSearchQuery({
    search: searchState.line,
    productionAreaId: currentProductionAreaId,
  })
  const {data: equipments = [], isFetching: isFetchingEquipments} = useGetEquipmentsWithSearchQuery(
    {
      search: searchState.equipment,
      productionAreaId: currentProductionAreaId,
      lineId: currentLineId,
    },
  )
  const [trigger, {data: anomalies, isFetching: anomaliesIsFetching, error: anomaliesError}] =
    useLazyGetAnomaliesQuery()

  const productionAreas = useMemo(() => {
    return Array.isArray(productionAreasRaw)
      ? productionAreasRaw
      : (productionAreasRaw?.results ?? [])
  }, [productionAreasRaw])

  const selectedNames = useMemo(() => {
    const selectedProductionArea =
      productionAreas.find((a) => a.id === currentProductionAreaId)?.name ?? ''
    const selectedLine = lines.find((l) => l.id === currentLineId)?.name ?? ''
    return {
      selectedProductionArea,
      selectedParentForEquipment: selectedLine || selectedProductionArea,
    }
  }, [productionAreas, lines, currentProductionAreaId, currentLineId])

  const updateHierarchyParam = useCallback(
    (key: string, value: string) => {
      if (!embedded) {
        return
      }
      setLocalFilters((prev) => {
        if (key === 'productionAreaId') {
          return {
            ...prev,
            productionAreaId: value,
            lineId: '',
            equipmentId: '',
          }
        }
        if (key === 'lineId') {
          return {
            ...prev,
            lineId: value,
            equipmentId: '',
          }
        }
        if (key === 'equipmentId') {
          return {
            ...prev,
            equipmentId: value,
          }
        }
        return prev
      })
      setPaginationModel((prev) => ({...prev, page: 0}))
    },
    [embedded],
  )

  const searchHandlers = useMemo(
    () => ({
      handleAreaSearch: (area: string): void =>
        setSearchState((prev) => ({
          ...prev,
          area,
        })),
      handleLineSearch: (line: string): void =>
        setSearchState((prev) => ({
          ...prev,
          line,
        })),
      handleEquipmentSearch: (equipment: string): void =>
        setSearchState((prev) => ({
          ...prev,
          equipment,
        })),
    }),
    [],
  )

  const triggerFunction = (): void => {
    const status = currentStatus
    const detection_type = currentDetectionType
    trigger({
      machineId: currentLineId === 'null' ? undefined : currentLineId,
      machineSubSystemId: currentEquipmentId === 'null' ? undefined : currentEquipmentId,
      productionAreaId:
        currentProductionAreaId === 'null' ? undefined : currentProductionAreaId || undefined,
      offset: paginationModel.page * paginationModel.pageSize,
      limit: paginationModel.pageSize,
      status: !status || status === 'All' ? '' : status,
      anomaly_detection_type: !detection_type || detection_type === 'All' ? '' : detection_type,
      ordering:
        sortModel[0]?.field && sortModel[0]?.sort
          ? `${sortModel[0].sort === 'desc' ? '-' : ''}${ANOMALY_ORDERING_FIELD_MAP[sortModel[0].field] || sortModel[0].field}`
          : undefined,
    })
  }

  useEffect(() => {
    triggerFunction()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentEquipmentId,
    currentLineId,
    currentProductionAreaId,
    currentStatus,
    currentDetectionType,
    paginationModel,
    searchParams,
  ])

  const columns: GridColDef[] = [
    {
      field: 'created_at',
      headerName: t('created_on'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) =>
        row
          ? getFormattedDate(row.created_at, currentUser, {
              showDate: true,
              showYear: true,
              showTime: true,
            })
          : '',
    },
    {
      field: 'machine',
      headerName: t('line'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) => (row ? row.machine.name : ''),
    },
    {
      field: 'machine_sub_system',
      headerName: t('equipment'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) => (row ? row.machine_sub_system.name : ''),
    },
    {
      field: 'program',
      headerName: t('program'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) => (row ? row.program.name : ''),
    },
    {
      field: 'batch',
      headerName: t('batch'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) => (row ? row.batch.name : ''),
    },
    {
      field: 'signal',
      headerName: t('signal'),
      editable: false,
      flex: 1,
      sortable: false,
      valueGetter: (_, row) => (row ? row.signal.name : ''),
    },
    {
      field: 'start',
      headerName: t('start'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) =>
        row && row.start
          ? getFormattedDate(row.start, currentUser, {
              showDate: true,
              showYear: true,
              showTime: true,
            })
          : '',
    },
    {
      field: 'end',
      headerName: t('end'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) =>
        row && row.end
          ? getFormattedDate(row.end, currentUser, {
              showDate: true,
              showYear: true,
              showTime: true,
            })
          : '',
    },
    {
      field: 'step',
      headerName: t('step'),
      editable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => (
        <StepDescription
          step={params.row.step}
          stepDescription={params.row.step_description?.description}
        />
      ),
    },
    {
      field: 'anomaly_detection_type',
      headerName: t('detection_type'),
      editable: false,
      // Historical anomalies can carry a null or unexpected detection type —
      // t() throws on unknown keys, which would crash the whole table.
      valueGetter: (_, row) =>
        row.anomaly_detection_type && i18n.exists(`anomalies:${row.anomaly_detection_type}`)
          ? t(row.anomaly_detection_type)
          : '',
    },
    {
      field: 'score',
      headerName: t('score'),
      editable: false,
      flex: 1,
    },
    {
      field: 'weighted_score',
      headerName: t('weighted_score'),
      editable: false,
      flex: 1,
      valueGetter: (_, row) =>
        row?.weighted_score != null ? Math.round(row.weighted_score * 100) / 100 : '',
    },
    {
      field: 'criticality',
      headerName: t('criticality'),
      editable: false,
      flex: 1,
      renderCell: (params: GridRenderCellParams) => {
        const level = params.row?.criticality
        if (!level) return null
        return (
          <Chip label={getCriticalityLabel(level)} size="small" sx={getCriticalityChipSx(level)} />
        )
      },
    },
    {
      field: 'reason',
      headerName: t('reason'),
      flex: 2,
    },
    {
      field: 'is_valid_anomaly',
      headerName: t('is_valid_anomaly'),
      flex: 0.5,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <TrueFalseIconBasicComponent value={params.row.is_valid_anomaly} />
      ),
    },
    {
      field: 'chat',
      headerName: '',
      width: 60,
      renderCell: (params: GridRenderCellParams) => (
        <AnomalyChatModal
          row={params.row}
          triggerFunction={triggerFunction}
          isFetching={anomaliesIsFetching}
        />
      ),
      disableColumnMenu: true,
      sortable: false,
    },
    {
      field: 'go_to_anomaly',
      headerName: '',
      width: 60,
      renderCell: (params: GridRenderCellParams) => <GoToAnomaly row={params.row} />,
      disableColumnMenu: true,
      sortable: false,
    },
  ]

  if (hideChat) {
    const chatIndex = columns.findIndex((col) => col.field === 'chat')
    if (chatIndex >= 0) {
      columns.splice(chatIndex, 1)
    }
  }
  if (hideGoTo) {
    const goToIndex = columns.findIndex((col) => col.field === 'go_to_anomaly')
    if (goToIndex >= 0) {
      columns.splice(goToIndex, 1)
    }
  }
  if (onSelect) {
    columns.push({
      field: 'select_anomaly',
      headerName: '',
      width: 90,
      renderCell: (params: GridRenderCellParams) => {
        const isSelected = selectedIds.includes(params.row.id)
        return (
          <Button
            size="small"
            variant="text"
            onClick={() => onSelect(params.row)}
            disabled={isSelected}
          >
            {isSelected ? 'Added' : 'Add'}
          </Button>
        )
      },
      disableColumnMenu: true,
      sortable: false,
    })
  }

  const statusOptions = [
    {
      key: 'All',
      value: 'All',
      label: t('anomaly_feedback_chat.all'),
    },
    {
      key: 'Detected',
      value: 'DETECTED',
      label: t('anomaly_feedback_chat.detected'),
    },
    {
      key: 'Classified',
      value: 'CLASSIFIED',
      label: t('anomaly_feedback_chat.classified'),
    },
  ]

  const detectionOptions = [
    {
      key: 'All',
      value: 'All',
      label: t('anomaly_feedback_chat.all'),
    },
    {
      key: 'step_signal',
      value: 'step_signal',
      label: t('step_signal'),
    },
    {
      key: 'value_based',
      value: 'value_based',
      label: t('value_based'),
    },
  ]

  return (
    <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          mb: 2,
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          flex: '0 0 auto',
        }}
        data-testid="anomaly-table-box"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          {embedded ? (
            <BasicHierarchyFiltersRow
              filterState={{
                productionAreaId: currentProductionAreaId,
                lineId: currentLineId,
                equipmentId: currentEquipmentId,
              }}
              productionAreas={productionAreas}
              lines={lines}
              equipments={equipments}
              searchHandlers={searchHandlers}
              loading={{isFetchingAreas, isFetchingLines, isFetchingEquipments}}
              selectedNames={{
                selectedProductionArea: selectedNames.selectedProductionArea,
                selectedParentForEquipment: selectedNames.selectedParentForEquipment,
              }}
              paramKeys={{
                PRODUCTION_AREA: 'productionAreaId',
                LINE: 'lineId',
                EQUIPMENT: 'equipmentId',
              }}
              updateParam={updateHierarchyParam}
            />
          ) : (
            <BasicHierarchyFilterGroup />
          )}
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          {embedded ? (
            <>
              <Picker
                hideSearch
                label={t('anomaly_feedback_chat.status_string')}
                value={currentStatus}
                options={statusOptions.map((option) => ({
                  id: option.value,
                  name: option.label,
                }))}
                onChange={(value) => {
                  setLocalFilters((prev) => ({...prev, status: value as string}))
                  setPaginationModel((prev) => ({...prev, page: 0}))
                }}
                displayLabel={false}
                size="small"
                clearable={false}
              />
              <Picker
                hideSearch
                label="Detection Type"
                value={currentDetectionType}
                options={detectionOptions.map((option) => ({
                  id: option.value,
                  name: option.label,
                }))}
                onChange={(value) => {
                  setLocalFilters((prev) => ({...prev, detectionType: value as string}))
                  setPaginationModel((prev) => ({...prev, page: 0}))
                }}
                displayLabel={false}
                size="small"
                clearable={false}
              />
            </>
          ) : (
            <>
              <AnomalyFilterByStatus />
              <AnomalyFilterByDetectionType />
            </>
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          height: height ?? (embedded ? 480 : '100%'),
          display: 'flex',
          flexDirection: 'column',
          ...(embedded ? {} : {flex: '1 1 0%', minHeight: 0}),
        }}
      >
        <BasicDataGrid
          data-testid="mbai-main-line-management-signals-table"
          data-cy="mbai-main-line-management-signals-table"
          title=""
          titleSearchParams=""
          columnWidthsKey="anomaly_table"
          hideHeader
          hideSearch
          height="100%"
          rows={anomalies?.results || []}
          rowCount={anomalies?.count ?? 0}
          columns={columns}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={(model) => {
            setSortModel(model)
            setPaginationModel((prev) => ({...prev, page: 0}))
          }}
          rowHeight={tablesFullPage.rowHeight}
          disableRowSelectionOnClick
          pageSizeOptions={STANDARD_PAGE_SIZE_OPTIONS}
          loading={
            anomaliesIsFetching || isFetchingAreas || isFetchingLines || isFetchingEquipments
          }
          queryError={anomaliesError}
          noRowsMessage={tCommon('no_data')}
        />
      </Box>
    </Box>
  )
}

const ANOMALY_ORDERING_FIELD_MAP: Record<string, string> = {
  created_at: 'created_at',
  machine: 'batch__machine__name',
  machine_sub_system: 'batch__program__machine_sub_system__name',
  program: 'batch__program__name',
  batch: 'batch__name',
  start: 'start',
  end: 'end',
  step: 'step',
  anomaly_detection_type: 'anomaly_detection_type',
  score: 'score',
  weighted_score: 'weighted_score',
  criticality: 'criticality',
  reason: 'reason',
}

export default AnomalyTable
